import { useCallback, useMemo } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { LEVELS, RATING, TROPHIES, type Stats } from '@il-colpo/meta/progress';
import { AvatarFigure } from '../../account/AvatarFigure.tsx';
import { useAccount } from '../../account/accountStore.ts';
import { useLooseT } from '../../game/text/loose.ts';
import { useMetaClient, useRemote } from '../../meta/hooks.ts';
import { useFlag } from '../../meta/liveStore.ts';
import type {
  ChallengeStanding,
  PlayerProgress,
  RatingSummary,
  RecentMatch,
} from '../../meta/types.ts';
import { Badge, Panel, ProgressBar, ScreenFrame } from '../../ui/index.ts';
import { useDateFormat } from '../account/common.tsx';
import { Failed, GatePanel, Page, challengeTitle, useMetaGate, useProgress } from './common.tsx';
import styles from './Meta.module.css';

const STAT_ORDER: readonly (keyof Stats)[] = [
  'matches',
  'heists',
  'wins',
  'loot',
  'bestLoot',
  'asInfiltrator',
  'infiltratorWins',
  'unmasksRight',
  'unmasksWrong',
  'abandoned',
  'dailyRuns',
];

function LevelPanel({ progress }: { progress: PlayerProgress }) {
  const { t } = useTranslation('meta');
  const profile = useAccount((s) => s.profile);
  const { level } = progress;
  const atCeiling = level.level >= LEVELS.maxLevel;
  return (
    <Panel>
      <div className={styles['stack']}>
        <div className={styles['levelHead']}>
          {profile ? <AvatarFigure avatar={profile.avatar} size="4.5rem" /> : null}
          <div className={styles['levelText']}>
            <span className={styles['levelNumber']}>
              {t('progress.level', { level: level.level })}
            </span>
            <span className={styles['note']}>{t('progress.xp', { xp: progress.stats.xp })}</span>
          </div>
        </div>
        <ProgressBar value={level.fraction * 100} label={t('progress.levelBar')} />
        {atCeiling ? null : (
          <p className={styles['note']}>
            {t('progress.toNext', { needed: level.needed - level.into, next: level.level + 1 })}
          </p>
        )}
      </div>
    </Panel>
  );
}

function RatingCard({ rating, seasonNumber }: { rating: RatingSummary; seasonNumber: number }) {
  const { t } = useTranslation('meta');
  const tm = useLooseT('meta');
  return (
    <div className={styles['rating']}>
      <span className={styles['ratingKind']}>{tm(`ratingKinds.${rating.kind}`)}</span>
      <span className={styles['ratingNumber']}>{rating.rating}</span>
      <div className={styles['row']}>
        {rating.placed && rating.league ? (
          <Badge tone="amber">{tm(`leagues.${rating.league}`)}</Badge>
        ) : (
          <span className={styles['note']}>
            {t('progress.placing', { games: rating.games, total: RATING.placementGames })}
          </span>
        )}
        <span className={styles['note']}>{t('progress.season', { n: seasonNumber })}</span>
      </div>
    </div>
  );
}

function ChallengeRow({ standing }: { standing: ChallengeStanding }) {
  const { t } = useTranslation('meta');
  const tm = useLooseT('meta');
  const g = useLooseT('game');
  const { def, progress, completed } = standing;
  return (
    <li className={styles['challenge']} data-done={completed}>
      <div className={styles['challengeTop']}>
        <strong>{challengeTitle(def, tm, g)}</strong>
        {completed ? (
          <Badge tone="green">{t('progress.done')}</Badge>
        ) : (
          <Badge tone="neutral">{t('progress.reward', { xp: def.rewardXp })}</Badge>
        )}
      </div>
      <ProgressBar
        value={(progress / def.goal) * 100}
        label={challengeTitle(def, tm, g)}
        tone={completed ? 'default' : 'blueprint'}
      />
      <span className={styles['note']}>{`${String(progress)} / ${String(def.goal)}`}</span>
    </li>
  );
}

function ChallengesPanel({ progress }: { progress: PlayerProgress }) {
  const { t } = useTranslation('meta');
  const daily = progress.challenges.filter((c) => c.def.period === 'daily');
  const weekly = progress.challenges.filter((c) => c.def.period === 'weekly');
  return (
    <Panel title={t('progress.challenges')}>
      <div className={styles['stack']}>
        <h3 className={styles['subtitle']}>{t('progress.daily')}</h3>
        <ul className={styles['list']}>
          {daily.map((c) => (
            <ChallengeRow key={c.key} standing={c} />
          ))}
        </ul>
        <h3 className={styles['subtitle']}>{t('progress.weekly')}</h3>
        <ul className={styles['list']}>
          {weekly.map((c) => (
            <ChallengeRow key={c.key} standing={c} />
          ))}
        </ul>
      </div>
    </Panel>
  );
}

function StatsPanel({ stats }: { stats: Stats }) {
  const { t } = useTranslation('meta');
  const tm = useLooseT('meta');
  return (
    <Panel title={t('progress.stats')}>
      <dl className={styles['stats']}>
        {STAT_ORDER.map((key) => (
          <div key={key} className={styles['stat']}>
            <dt>{tm(`stats.${key}`)}</dt>
            <dd>
              {key === 'loot' || key === 'bestLoot'
                ? t('stats.lootUnit', { amount: stats[key] })
                : stats[key]}
            </dd>
          </div>
        ))}
      </dl>
    </Panel>
  );
}

function TrophiesPanel({ owned }: { owned: readonly string[] }) {
  const { t } = useTranslation('meta');
  const tm = useLooseT('meta');
  const have = new Set(owned);
  return (
    <Panel title={t('progress.trophies')}>
      <div className={styles['stack']}>
        <p className={styles['note']}>
          {t('progress.trophiesCount', {
            owned: TROPHIES.filter((x) => have.has(x.id)).length,
            total: TROPHIES.length,
          })}
        </p>
        <ul className={styles['cards']}>
          {TROPHIES.map((trophy) => {
            const got = have.has(trophy.id);
            return (
              <li key={trophy.id} className={styles['card']} data-state={got ? 'owned' : 'locked'}>
                <h3 className={styles['cardTitle']}>{tm(`trophy.${trophy.id}.name`)}</h3>
                <p className={styles['cardBody']}>{tm(`trophy.${trophy.id}.desc`)}</p>
                {got ? <Badge tone="amber">{t('progress.done')}</Badge> : null}
              </li>
            );
          })}
        </ul>
      </div>
    </Panel>
  );
}

function RecentPanel() {
  const { t } = useTranslation('meta');
  const replaysOn = useFlag('replays');
  const tm = useLooseT('meta');
  const g = useLooseT('game');
  const { date } = useDateFormat();
  const meta = useMetaClient();
  const load = useMemo(() => (meta ? () => meta.recentMatches(8) : null), [meta]);
  const recent = useRemote<RecentMatch[]>(load);
  const retry = useCallback(() => {
    recent.reload();
  }, [recent]);
  return (
    <Panel title={t('progress.recent')}>
      {recent.status === 'error' ? (
        <Failed onRetry={retry} />
      ) : recent.data && recent.data.length > 0 ? (
        <ul className={styles['list']}>
          {recent.data.map((m) => (
            <li key={m.id} className={styles['recent']}>
              <span>
                <strong>{g(`maps.${m.mapId}.name`)}</strong>
                {` · ${tm(`modes.${m.mode}`)} · ${g(`difficulty.${m.difficulty}`)}`}
              </span>
              <span className={styles['row']}>
                <Badge tone={m.abandoned ? 'neutral' : m.won ? 'green' : 'neutral'}>
                  {m.abandoned
                    ? t('progress.left')
                    : m.won
                      ? t('progress.won')
                      : t('progress.lost')}
                </Badge>
                <span className={styles['note']}>{date(m.endedAt)}</span>
                {m.xpGain > 0 ? (
                  <Badge tone="cyan">{t('progress.xp', { xp: m.xpGain })}</Badge>
                ) : null}
                {replaysOn ? <Link to={`/replay/${m.id}`}>{t('progress.replay')}</Link> : null}
              </span>
            </li>
          ))}
        </ul>
      ) : recent.status === 'ready' ? (
        <p className={styles['note']}>{t('progress.recentEmpty')}</p>
      ) : (
        <p className={styles['note']} role="status">
          {t('gate.loading')}
        </p>
      )}
    </Panel>
  );
}

/** `/progress`: the level, the ranked ratings, the challenges, the rap sheet, the trophies, the latest heists. */
export function ProgressScreen() {
  const { t } = useTranslation('meta');
  const gate = useMetaGate();
  const progress = useProgress(gate === 'ready');
  const seasonNumber = progress.data ? Number(progress.data.seasonId.slice(1)) : 1;
  return (
    <ScreenFrame title={t('progress.title')} backTo="/account">
      <Page>
        {gate !== 'ready' ? <GatePanel gate={gate} /> : null}
        {gate === 'ready' && progress.status === 'loading' ? <GatePanel gate="loading" /> : null}
        {gate === 'ready' && progress.status === 'error' ? (
          <Failed onRetry={progress.reload} />
        ) : null}
        {progress.data ? (
          <>
            <LevelPanel progress={progress.data} />
            <Panel title={t('progress.ratings')}>
              <div className={styles['stack']}>
                <p className={styles['note']}>{t('progress.ratingsHint')}</p>
                <div className={styles['cols']}>
                  {progress.data.ratings.map((r) => (
                    <RatingCard key={r.kind} rating={r} seasonNumber={seasonNumber} />
                  ))}
                </div>
              </div>
            </Panel>
            <ChallengesPanel progress={progress.data} />
            <StatsPanel stats={progress.data.stats} />
            <TrophiesPanel owned={progress.data.trophies} />
            <RecentPanel />
            <div className={styles['row']}>
              <Link to="/collection">{t('progress.toCollection')}</Link>
              <Link to="/leaderboards">{t('progress.toLeaderboards')}</Link>
            </div>
          </>
        ) : null}
      </Page>
    </ScreenFrame>
  );
}
